import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../theme";
import { Gamepad2, Users, Zap, Trophy } from "lucide-react";

function About() {
  const { colors } = useContext(ThemeContext)!;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  return (
    <div className={`min-h-screen ${colors.bgSecondaryColor} relative overflow-hidden`}>
      {/* Background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-24 left-16 w-72 h-72 bg-purple-500 rounded-full blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-24 right-16 w-96 h-96 bg-blue-500 rounded-full blur-3xl opacity-20 animate-pulse" />
      </div>

      <div className="relative z-10 container mx-auto px-6 py-4 md:py-20">
        {/* Header */}
        <div
          className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
        >
          <h1 className={`text-4xl md:text-5xl font-black ${colors.textColor} mb-6`}>
            About I Call On
          </h1>
          <p className={`text-xl md:text-2xl ${colors.textColor} opacity-80 leading-relaxed`}>
            A fast-thinking, high-energy word game where creativity, speed, and strategy collide.
          </p>
        </div>

        {/* What is I Call On */}
        <section className="mt-4 md:mt-20 max-w-5xl mx-auto">
          <div className={`${colors.cardBg} ${colors.borderColor} border rounded-3xl p-6 md:p-10 shadow-xl`}>
            <h2 className={`text-3xl font-bold ${colors.textColor} mb-4`}>
              What is I Call On?
            </h2>
            <p className={`${colors.textColor} opacity-80 text-lg leading-relaxed`}>
              I Call On is a modern multiplayer word game designed to test how fast you think and
              how creative you can be under pressure. Each round challenges players to come up
              with words that match specific categories, all starting with a randomly chosen letter.
              The clock is ticking, the competition is real, and hesitation costs points.
            </p>
            <p className={`${colors.textColor} opacity-80 text-lg leading-relaxed mt-4`}>
              Whether you’re playing with close friends, family, or competitive rivals, every game
              feels different. No two rounds play the same, and no player wins by luck alone.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="mt-20 grid md:grid-cols-3 gap-8">
          <div className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8`}>
            <Gamepad2 className="h-10 w-10 text-blue-500 mb-4" />
            <h3 className={`text-2xl font-bold ${colors.textColor} mb-2`}>
              Simple Rules
            </h3>
            <p className={`${colors.textColor} opacity-70`}>
              Join a game, get a letter, and fill in categories with words that start with it.
              Easy to learn, hard to master.
            </p>
          </div>

          <div className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8`}>
            <Zap className="h-10 w-10 text-purple-500 mb-4" />
            <h3 className={`text-2xl font-bold ${colors.textColor} mb-2`}>
              Fast Rounds
            </h3>
            <p className={`${colors.textColor} opacity-70`}>
              Time-limited rounds keep the energy high. Think fast, type faster, and trust your instincts.
            </p>
          </div>

          <div className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8`}>
            <Trophy className="h-10 w-10 text-green-500 mb-4" />
            <h3 className={`text-2xl font-bold ${colors.textColor} mb-2`}>
              Smart Scoring
            </h3>
            <p className={`${colors.textColor} opacity-70`}>
              Unique answers score higher. Creativity beats repetition, and originality separates winners.
            </p>
          </div>
        </section>

        {/* Why people love it */}
        <section className="mt-20 max-w-5xl mx-auto">
          <div className={`${colors.cardBg} ${colors.borderColor} border rounded-3xl p-10 shadow-xl`}>
            <h2 className={`text-3xl font-bold ${colors.textColor} mb-4`}>
              Why Players Love I Call On
            </h2>
            <ul className={`${colors.textColor} opacity-80 text-lg space-y-3 list-disc list-inside`}>
              <li>Perfect balance of fun and competition</li>
              <li>Play with friends anywhere in the world</li>
              <li>Every round feels fresh and unpredictable</li>
              <li>Great for casual games or serious bragging rights</li>
              <li>Designed for both quick sessions and long game nights</li>
            </ul>
          </div>
        </section>

        {/* Community */}
        <section className="mt-20 text-center max-w-4xl mx-auto">
          <Users className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h2 className={`text-3xl font-bold ${colors.textColor} mb-4`}>
            Built for Friends, Fueled by Competition
          </h2>
          <p className={`${colors.textColor} opacity-80 text-lg leading-relaxed`}>
            I Call On isn’t just a game. It’s a shared experience. A place where inside jokes are born,
            rivalries grow, and quick thinking earns respect. Whether you’re here to laugh or to win,
            there’s always a reason to play one more round.
          </p>
        </section>
      </div>
    </div>
  );
}

export default About;
